/**
 * useToggleState Hook
 * Manages toggle button state with controlled/uncontrolled support
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export interface UseToggleStateProps {
  onSelectedChange?: (selected: boolean) => void;
  selected?: boolean;
  toggle: boolean;
}

export interface UseToggleStateReturn {
  handleToggle: () => void;
  isSelected: boolean;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for managing toggle button state
 * Supports both controlled and uncontrolled modes
 */
export function useToggleState({
  onSelectedChange,
  selected: controlledSelected,
  toggle,
}: UseToggleStateProps): UseToggleStateReturn {
  const [internalSelected, setInternalSelected] = useState(false);

  const isSelected = useMemo(() => {
    if (!toggle) return false;
    return controlledSelected !== undefined ? controlledSelected : internalSelected;
  }, [controlledSelected, internalSelected, toggle]);

  // Ref to track current state without causing handleToggle to recreate
  const stateRef = useRef({ isSelected, controlledSelected, onSelectedChange, toggle });

  useEffect(() => {
    stateRef.current = { isSelected, controlledSelected, onSelectedChange, toggle };
  });

  const handleToggle = useCallback(() => {
    const {
      isSelected: currentSelected,
      controlledSelected: currentControlled,
      onSelectedChange: currentOnChange,
      toggle: currentToggle,
    } = stateRef.current;

    if (!currentToggle) return;

    const newSelected = !currentSelected;
    if (currentControlled === undefined) {
      setInternalSelected(newSelected);
    }
    currentOnChange?.(newSelected);
  }, []); // Stable reference - never recreates

  return { handleToggle, isSelected };
}

export default useToggleState;
